import { yupResolver } from '@hookform/resolvers/yup';
import LoadingButton from '@mui/lab/LoadingButton';
import {
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  FormHelperText,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import _ from 'lodash';
import { type FC, useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useDebounce } from 'usehooks-ts';
import * as yup from 'yup';

import PatientActivityCostEstimate from './PatientActivityCostEstimate.js';
import {
  ACTIVITY_CLASSIFY_STRATEGIES,
  ACTIVITY_MERGE_LAYOUTS,
  type ActivityClassifyStrategy,
  type ActivityMergeLayout,
  DEFAULT_ACTIVITY_CLASSIFY_STRATEGY,
  DEFAULT_ACTIVITY_MERGE_LAYOUT,
  type GetActivityCostEstimateParams,
  useActivityCostEstimate,
  useActivityModels,
  useUpdateActivityConfig,
} from '../../api/index.js';
import useSnackBar from '../../hooks/use-snack-bar.js';

// Bounds mirror the backend Zod schema (`updateActivityConfigBody`):
//   intervalMinutes: 3..60, clipDurationSeconds: 8..30 — both required when enabled=true.
const MIN_INTERVAL = 3;
const MAX_INTERVAL = 60;
const DEFAULT_INTERVAL = 5;
const MIN_CLIP_SECONDS = 8;
const MAX_CLIP_SECONDS = 30;
const DEFAULT_CLIP_SECONDS = 8;
const MAX_AI_CONTEXT_LENGTH = 2000;
const ESTIMATE_DEBOUNCE_MS = 400;
// A newly enabled patient's daily cap is the estimated day plus room for a
// longer-than-usual response, so an ordinary day never trips it.
const DAILY_CAP_HEADROOM = 1.5;
const MIN_DERIVED_DAILY_CAP_USD = 0.01;

function toNumberOrNull(value: unknown): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function isBetween(value: number, min: number, max: number): boolean {
  return value >= min && value <= max;
}

function roundToCents(value: number): number {
  return Math.round(value * 100) / 100;
}

const DAILY_CAP_HELPER_TEXT =
  'Leave blank for no cap. When today’s estimated spend hits this value, captures pause until midnight UTC.';
const DERIVED_CAP_HELPER_TEXT =
  `Suggested from the estimate (${DAILY_CAP_HEADROOM}× a day). Edit freely, or leave blank for no cap.`;

const numericNullable = () =>
  yup
    .number()
    .nullable()
    .transform((value, original) => (original === '' || original === null ? null : value));

// `intervalMinutes` / `clipDurationSeconds` are allowed to be empty in the form
// (operator unchecking "enabled" doesn't need to clear them), so we accept
// `null` on the form layer and only enforce the range when `enabled` is true.
const activityConfigSchema = yup.object({
  enabled: yup.boolean().required(),
  intervalMinutes: numericNullable().when('enabled', {
    is: true,
    then: (schema) =>
      schema
        .typeError('Capture interval is required when AI monitoring is on.')
        .min(MIN_INTERVAL, `Capture interval must be at least ${MIN_INTERVAL} minutes.`)
        .max(MAX_INTERVAL, `Capture interval must be at most ${MAX_INTERVAL} minutes.`)
        .required('Capture interval is required when AI monitoring is on.'),
    otherwise: (schema) => schema.notRequired(),
  }),
  clipDurationSeconds: numericNullable().when('enabled', {
    is: true,
    then: (schema) =>
      schema
        .typeError('Clip length is required when AI monitoring is on.')
        .min(MIN_CLIP_SECONDS, `Clip length must be at least ${MIN_CLIP_SECONDS} seconds.`)
        .max(MAX_CLIP_SECONDS, `Clip length must be at most ${MAX_CLIP_SECONDS} seconds.`)
        .required('Clip length is required when AI monitoring is on.'),
    otherwise: (schema) => schema.notRequired(),
  }),
  aiContext: yup
    .string()
    .max(MAX_AI_CONTEXT_LENGTH, `Context must be at most ${MAX_AI_CONTEXT_LENGTH} characters.`)
    .nullable(),
  dailyCostCapUsd: numericNullable().moreThan(0, 'Daily cap must be > 0 (leave blank for no cap).'),
  modelId: yup.string().required(),
  classifyStrategy: yup.string().oneOf(['per_camera', 'merged', 'vision'] as const).required(),
  mergeLayout: yup.string().oneOf(['grid', 'sequence'] as const).required(),
});

type ActivityConfigFormValues = yup.InferType<typeof activityConfigSchema>;

interface PatientActivityConfigFormProps {
  patientId: string;
  enabled: boolean;
  intervalMinutes: number | null;
  clipDurationSeconds: number | null;
  aiContext: string | null;
  dailyCostCapUsd: number | null;
  modelId: string | null;
  classifyStrategy: ActivityClassifyStrategy | null;
  mergeLayout: ActivityMergeLayout | null;
  canUpdate: boolean;
  onDirtyChange?: (isDirty: boolean) => void;
}

const PatientActivityConfigForm: FC<PatientActivityConfigFormProps> = ({
  patientId,
  enabled,
  intervalMinutes,
  clipDurationSeconds,
  aiContext,
  dailyCostCapUsd,
  modelId,
  classifyStrategy,
  mergeLayout,
  canUpdate,
  onDirtyChange,
}) => {
  const snackBar = useSnackBar();
  const mutation = useUpdateActivityConfig(patientId);
  const models = useActivityModels();
  const defaultModelId = models.data?.defaultId;

  const {
    control,
    handleSubmit,
    reset,
    watch,
    getValues,
    setValue,
    formState: { errors, isDirty, isSubmitting },
  } = useForm<ActivityConfigFormValues>({
    resolver: yupResolver(activityConfigSchema),
    defaultValues: {
      enabled,
      intervalMinutes: intervalMinutes ?? DEFAULT_INTERVAL,
      clipDurationSeconds: clipDurationSeconds ?? DEFAULT_CLIP_SECONDS,
      aiContext,
      dailyCostCapUsd,
      modelId: modelId ?? defaultModelId ?? '',
      classifyStrategy: classifyStrategy ?? DEFAULT_ACTIVITY_CLASSIFY_STRATEGY,
      mergeLayout: mergeLayout ?? DEFAULT_ACTIVITY_MERGE_LAYOUT,
    },
  });

  // Keep the form in sync when the upstream patient row refetches (e.g. after
  // a successful save, or on initial load when the patient row arrives late).
  useEffect(() => {
    reset({
      enabled,
      intervalMinutes: intervalMinutes ?? DEFAULT_INTERVAL,
      clipDurationSeconds: clipDurationSeconds ?? DEFAULT_CLIP_SECONDS,
      aiContext,
      dailyCostCapUsd,
      modelId: modelId ?? defaultModelId ?? '',
      classifyStrategy: classifyStrategy ?? DEFAULT_ACTIVITY_CLASSIFY_STRATEGY,
      mergeLayout: mergeLayout ?? DEFAULT_ACTIVITY_MERGE_LAYOUT,
    });
  }, [
    enabled,
    intervalMinutes,
    clipDurationSeconds,
    aiContext,
    dailyCostCapUsd,
    modelId,
    defaultModelId,
    classifyStrategy,
    mergeLayout,
    reset,
  ]);

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  const isEnabled = watch('enabled');
  const watchedStrategy = watch('classifyStrategy');
  const watchedLayout = watch('mergeLayout');
  const watchedModelId = watch('modelId');
  const watchedInterval = watch('intervalMinutes');
  const watchedClip = watch('clipDurationSeconds');
  const isMerged = watchedStrategy === 'merged';

  const estimateParams = useMemo<GetActivityCostEstimateParams | null>(() => {
    const interval = toNumberOrNull(watchedInterval);
    const clip = toNumberOrNull(watchedClip);
    if (!isEnabled || !watchedModelId || interval === null || clip === null) {
      return null;
    }
    if (!isBetween(interval, MIN_INTERVAL, MAX_INTERVAL) || !isBetween(clip, MIN_CLIP_SECONDS, MAX_CLIP_SECONDS)) {
      return null;
    }
    return {
      intervalMinutes: interval,
      clipDurationSeconds: clip,
      modelId: watchedModelId,
      classifyStrategy: watchedStrategy,
      mergeLayout: watchedLayout,
    };
  }, [isEnabled, watchedModelId, watchedInterval, watchedClip, watchedStrategy, watchedLayout]);
  const debouncedEstimateParams = useDebounce(estimateParams, ESTIMATE_DEBOUNCE_MS);
  const estimate = useActivityCostEstimate(patientId, debouncedEstimateParams ?? {}, {
    enabled: debouncedEstimateParams !== null,
  });

  // The cap follows the estimate only while the patient is being enabled for
  // the first time and the operator has not typed a cap of their own.
  const isNewlyEnabled = isEnabled && !enabled && dailyCostCapUsd === null;
  const [derivedCap, setDerivedCap] = useState<number | null>(null);
  useEffect(() => {
    const costPerDayUsd = estimate.data?.costPerDayUsd ?? 0;
    if (!isNewlyEnabled || costPerDayUsd <= 0) {
      return;
    }
    const current = toNumberOrNull(getValues('dailyCostCapUsd'));
    if (current !== null && current !== derivedCap) {
      return;
    }
    const derived = Math.max(MIN_DERIVED_DAILY_CAP_USD, roundToCents(costPerDayUsd * DAILY_CAP_HEADROOM));
    if (current === derived) {
      return;
    }
    setDerivedCap(derived);
    setValue('dailyCostCapUsd', derived, { shouldDirty: true });
  }, [isNewlyEnabled, estimate.data, derivedCap, getValues, setValue]);

  const onSubmit = handleSubmit((values) => {
    mutation.mutate(
      {
        enabled: values.enabled,
        intervalMinutes: values.enabled ? Number(values.intervalMinutes) : values.intervalMinutes ?? undefined,
        clipDurationSeconds: values.enabled
          ? Number(values.clipDurationSeconds)
          : values.clipDurationSeconds ?? undefined,
        aiContext: values.aiContext || null,
        dailyCostCapUsd: values.dailyCostCapUsd ?? null,
        modelId: values.modelId,
        classifyStrategy: values.classifyStrategy,
        mergeLayout: values.mergeLayout,
      },
      {
        onSuccess: () => snackBar.success('AI monitoring settings saved.'),
        onError: () => snackBar.error('Failed to save AI monitoring settings.'),
      },
    );
  });

  return (
    <Box>
      <Typography variant="h5" component="h3">AI monitoring</Typography>
      <FormHelperText sx={{ mb: 2 }}>
        When enabled, clips are sampled from the patient&apos;s cameras only during check-in
        windows — once at the window start and every N minutes after — classified by an AI
        model, and stored as an ActivityEvent.
      </FormHelperText>

      <form onSubmit={onSubmit}>
        <Stack spacing={2}>
          <Controller
            name={'enabled'}
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!field.value}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                      field.onChange(event.target.checked)
                    }
                    disabled={!canUpdate}
                  />
                }
                label={'Enable AI monitoring'}
              />
            )}
          />

          <Controller
            name={'modelId'}
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                select
                value={field.value ?? ''}
                label={'AI model'}
                disabled={!isEnabled || !canUpdate || models.isLoading}
                helperText={'Video models analyse the clip; frame models analyse stills.'}
              >
                {(models.data?.items ?? []).map((m) => (
                  <MenuItem key={m.id} value={m.id}>
                    {m.label} ({m.inputMode})
                  </MenuItem>
                ))}
              </TextField>
            )}
          />

          <Controller
            name={'classifyStrategy'}
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                select
                value={field.value ?? ''}
                label={'Classification strategy'}
                disabled={!isEnabled || !canUpdate}
                helperText={
                  'The model can override this: TwelveLabs Pegasus always runs merged, and a ' +
                  'frame-only model falls back to per camera.'
                }
              >
                {ACTIVITY_CLASSIFY_STRATEGIES.map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    {s.label}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />

          <Controller
            name={'mergeLayout'}
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                select
                value={field.value ?? ''}
                label={'Merge layout'}
                disabled={!isEnabled || !isMerged || !canUpdate}
                helperText={
                  'Grid keeps one clip length whatever the camera count but shrinks each camera; ' +
                  'sequence keeps them full size and grows cost with each camera.'
                }
              >
                {ACTIVITY_MERGE_LAYOUTS.map((l) => (
                  <MenuItem key={l.id} value={l.id}>
                    {l.label}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />

          <Controller
            name={'intervalMinutes'}
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                value={field.value ?? ''}
                type={'number'}
                label={'Capture interval (minutes)'}
                inputProps={{ min: MIN_INTERVAL, max: MAX_INTERVAL, step: 1 }}
                disabled={!isEnabled || !canUpdate}
                error={!!errors?.intervalMinutes}
                helperText={
                  errors?.intervalMinutes?.message ||
                  `Between ${MIN_INTERVAL} and ${MAX_INTERVAL} minutes, aligned to the check-in window start.`
                }
              />
            )}
          />

          <Controller
            name={'clipDurationSeconds'}
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                value={field.value ?? ''}
                type={'number'}
                label={'Clip length (seconds)'}
                inputProps={{ min: MIN_CLIP_SECONDS, max: MAX_CLIP_SECONDS, step: 1 }}
                disabled={!isEnabled || !canUpdate}
                error={!!errors?.clipDurationSeconds}
                helperText={
                  errors?.clipDurationSeconds?.message ||
                  `Between ${MIN_CLIP_SECONDS} and ${MAX_CLIP_SECONDS} seconds recorded per camera on each tick.`
                }
              />
            )}
          />

          <Controller
            name={'aiContext'}
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                value={field.value ?? ''}
                multiline
                rows={4}
                label={'AI context'}
                inputProps={{ maxLength: MAX_AI_CONTEXT_LENGTH }}
                disabled={!isEnabled || !canUpdate}
                error={!!errors?.aiContext}
                helperText={
                  errors?.aiContext?.message ||
                  'Describe the patient and home so the AI can interpret what it sees.'
                }
              />
            )}
          />

          <Controller
            name={'dailyCostCapUsd'}
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                value={field.value ?? ''}
                type={'number'}
                label={'Daily cost cap (USD)'}
                inputProps={{ min: 0, step: 0.1 }}
                disabled={!isEnabled || !canUpdate}
                error={!!errors?.dailyCostCapUsd}
                helperText={
                  errors?.dailyCostCapUsd?.message ||
                  (isNewlyEnabled && derivedCap !== null ? DERIVED_CAP_HELPER_TEXT : DAILY_CAP_HELPER_TEXT)
                }
              />
            )}
          />

          {isEnabled ? (
            <PatientActivityCostEstimate
              estimate={estimate.data}
              isLoading={estimate.isLoading || estimate.isFetching}
              isError={estimate.isError}
            />
          ) : null}

          <Divider />
          <Stack direction={'row'} justifyContent={'flex-end'}>
            <LoadingButton
              type={'submit'}
              aria-label={'Save AI monitoring'}
              loading={isSubmitting || mutation.isLoading}
              variant={'contained'}
              disabled={!_.isEmpty(errors) || !isDirty || !canUpdate}
            >
              Save
            </LoadingButton>
          </Stack>
        </Stack>
      </form>
    </Box>
  );
};

export default PatientActivityConfigForm;
